import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import { adminProvenanceCoverage, adminProvenanceIssues, adminProvenanceOverride, adminProvenanceSync, type Provenance, type ProvenanceCoverage, type UrlStatus } from '../../../api/provenanceApi';
import { S, fmtWhen } from './adminPageStyles';

/** Source-URL governance (SRS #88): coverage per source, broken/missing links, corrections with audit. */

const AdminProvenancePage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const [cov, setCov] = useState<ProvenanceCoverage | null>(null);
  const [status, setStatus] = useState<UrlStatus>('broken');
  const [items, setItems] = useState<Provenance[]>([]);
  const [edit, setEdit] = useState<Record<string, { url: string; note: string }>>({});
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminProvenanceCoverage().then(setCov).catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
    adminProvenanceIssues(status).then(r => setItems(r.items)).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setBusy(true); setError(null); setOk(null);
    try { const r = await adminProvenanceSync(); setOk(`Mirrored ${r.sync.total} records (${r.sync.created} new); checked ${r.verify.checked} links — ${r.verify.ok} ok, ${r.verify.redirected} moved, ${r.verify.broken} broken.`); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed'); } finally { setBusy(false); }
  };
  const save = async (id: string, clear = false) => {
    const d = edit[id] || { url: '', note: '' };
    try { await adminProvenanceOverride(id, clear ? null : d.url, d.note); setOk(clear ? 'Override cleared.' : 'Source URL corrected and verified.'); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed'); }
  };

  const tile = (l: string, v: React.ReactNode) => (
    <div key={l} style={{ border: '1px solid var(--ds-border)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ds-text)' }}>{v}</div>
      <div style={{ fontSize: 12, color: 'var(--ds-text-muted)' }}>{l}</div>
    </div>
  );

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Source provenance" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>Source provenance</h2>
        <p style={S.sub}>Every regulation is traced to its original source URL (or, where the source publishes none, the canonical address built from its identifiers — congress.gov, legislation.gov.uk, LegiScan), the time we first retrieved it and a fingerprint of the text we hold. Links are verified nightly (stalest first); a link is called broken only after two failed checks; sites that block automated checks show as blocked, not broken. Correct a URL here — the original is kept and the change is audited.</p>
        {error && <div style={S.error}>{error}</div>}
        {ok && <div style={S.ok}>{ok}</div>}
        {cov && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 16 }}>
            {tile('Records', cov.total)}
            {tile('With source URL', `${cov.url_pct}%`)}
            {tile('Canonical links derived', cov.derived)}
            {tile('Missing URL', cov.missing)}
            {tile('Broken links', cov.broken)}
            {tile('Last check', cov.last_verified_at ? fmtWhen(cov.last_verified_at) : 'never')}
          </div>
        )}
        <div style={S.toolbar}>
          <button style={S.primary} onClick={sync} disabled={busy}>{busy ? 'Working…' : 'Sync & verify now'}</button>
          <a style={{ ...S.btn, textDecoration: 'none', color: 'var(--ds-text)' }} href={`${process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000'}/api/v1/admin/provenance/export.csv`}>Register (CSV)</a>
        </div>
        {cov && (
          <div style={{ overflowX: 'auto', marginBottom: 16 }}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Source</th><th style={S.th}>Records</th><th style={S.th}>With URL</th><th style={S.th}>Verified</th><th style={S.th}>Broken</th><th style={S.th}>Missing</th></tr></thead>
              <tbody>
                {cov.sources.map(s => (
                  <tr key={s.source_system}>
                    <td style={S.td}>{s.source_system}</td><td style={S.td}>{s.total}</td>
                    <td style={S.td}>{s.with_url} <span style={S.muted}>({s.url_pct}%)</span></td>
                    <td style={S.td}>{s.ok + s.redirected} <span style={S.muted}>({s.verified_pct}%)</span></td>
                    <td style={{ ...S.td, color: s.broken ? 'var(--ds-danger)' : undefined }}>{s.broken}</td>
                    <td style={{ ...S.td, color: s.missing ? 'var(--ds-warning)' : undefined }}>{s.missing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={S.tabs}>
          {(['broken', 'missing', 'blocked', 'redirected', 'unchecked'] as UrlStatus[]).map(t => <button key={t} style={S.tab(status === t)} onClick={() => setStatus(t)}>{t}</button>)}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Regulation</th><th style={S.th}>Source</th><th style={S.th}>URL</th><th style={S.th}>Last check</th><th style={S.th}>Correct</th></tr></thead>
            <tbody>
              {items.length === 0 && <tr><td style={S.td} colSpan={5}><span style={S.muted}>Nothing {status}.</span></td></tr>}
              {items.map(p => (
                <tr key={p.artifact_id}>
                  <td style={S.td}>{p.title || <span style={S.mono}>{p.artifact_id.slice(0, 8)}</span>}{p.override_by && <div style={{ ...S.muted, fontSize: 11 }}>corrected by {p.override_by}</div>}</td>
                  <td style={S.td}>{p.source_system}</td>
                  <td style={{ ...S.td, maxWidth: 320, wordBreak: 'break-all' }}>
                    {p.source_url ? <a href={p.source_url} target="_blank" rel="noreferrer">{p.source_url}</a> : <span style={S.muted}>—</span>}
                    {p.http_status && <div style={{ ...S.muted, fontSize: 11 }}>HTTP {p.http_status}{p.verify_error ? ` · ${p.verify_error}` : ''}</div>}
                    {p.final_url && p.url_status === 'redirected' && <div style={{ ...S.muted, fontSize: 11 }}>→ {p.final_url}</div>}
                  </td>
                  <td style={S.td}>{p.last_verified_at ? fmtWhen(p.last_verified_at) : '—'}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <input aria-label={`New URL for ${p.title || p.artifact_id}`} style={{ ...S.input, minWidth: 200 }} placeholder={p.final_url || 'https://…'} value={edit[p.artifact_id]?.url ?? ''} onChange={e => setEdit({ ...edit, [p.artifact_id]: { url: e.target.value, note: edit[p.artifact_id]?.note ?? '' } })} />
                      <input aria-label="Note" style={{ ...S.input, minWidth: 120 }} placeholder="note" value={edit[p.artifact_id]?.note ?? ''} onChange={e => setEdit({ ...edit, [p.artifact_id]: { url: edit[p.artifact_id]?.url ?? '', note: e.target.value } })} />
                      <button style={S.btn} onClick={() => save(p.artifact_id)} disabled={!edit[p.artifact_id]?.url}>Save</button>
                      {p.url_status === 'redirected' && p.final_url && <button style={S.btn} onClick={() => { setEdit({ ...edit, [p.artifact_id]: { url: p.final_url!, note: 'source moved' } }); }}>Use new address</button>}
                      {p.override_by && <button style={S.danger} onClick={() => save(p.artifact_id, true)}>Revert</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </DashboardLayout>
  );
};

export default AdminProvenancePage;
